/**
 * Shared helpers for API responses that return HTTP 2xx with
 * `{ status: "error", message?: string }` in the body (e.g. HTTP 201 + status error).
 *
 * Use for WarningModal business validation failures.
 * Do not use for HTTP 500 / network failures — leave those to the global API handler.
 */

/**
 * Normalize a response body to the record that carries `status`/`message`.
 * Some endpoints return the payload wrapped in a single-element array
 * (e.g. `[{ status: "error", message: "..." }]`), so unwrap that too.
 */
function getBodyRecord(data: unknown): Record<string, unknown> | null {
  const record = Array.isArray(data) ? data[0] : data;
  if (record && typeof record === "object" && !Array.isArray(record)) {
    return record as Record<string, unknown>;
  }
  return null;
}

export function isApiBodyStatusError(data: unknown): boolean {
  const record = getBodyRecord(data);
  if (!record) return false;
  // Case-insensitive: some endpoints return "Error"/"ERROR" or "fail"/"failed".
  const status = String(record.status ?? "").trim().toLowerCase();
  return status === "error" || status === "fail" || status === "failed";
}

export function getApiBodyErrorMessage(
  data: unknown,
  fallback: string,
): string {
  const record = getBodyRecord(data);
  if (record) {
    const message = String(record.message ?? "").trim();
    if (message) return message;
  }
  return fallback;
}

/** DB / SP / SQL jargon that must not appear in WarningModal or toasts. */
const TECHNICAL_SP_MESSAGE_PATTERNS = [
  /error executing\s+[\w_]+/i,
  /\[[\w_]+_ssp\]/i,
  /generate_[\w_]+_ssp/i,
  /operator does not exist/i,
  /pl\/pgsql/i,
  /syntax error/i,
  /sqlstate/i,
  /character varying/i,
  /invalid input syntax/i,
  /column\s+.+\s+does not exist/i,
  /function\s+.+\s+does not exist/i,
  /relation\s+.+\s+does not exist/i,
  /permission denied for/i,
  /\bcontext:/i,
  /at character \d+/i,
  /error_details/i,
  /^\s*[\[{]/,
] as const;

/** True when an API/SP message is internal-only (Postgres, SP name, stack context). */
export function isTechnicalSpErrorMessage(message: string): boolean {
  const trimmed = String(message ?? "").trim();
  if (!trimmed) return false;
  return TECHNICAL_SP_MESSAGE_PATTERNS.some((pattern) => pattern.test(trimmed));
}

/**
 * Returns a safe user-facing message for WarningModal.
 * Technical SP/DB errors are replaced with `fallback`; business copy is kept as-is.
 */
export function getUserFacingSpErrorMessage(
  message: string,
  fallback: string,
): string {
  const trimmed = String(message ?? "").trim();
  if (!trimmed) return fallback;
  if (isTechnicalSpErrorMessage(trimmed)) return fallback;
  return trimmed;
}

/** Unwrap `{ status, message }` and sanitize for end users. */
export function getUserFacingApiBodyErrorMessage(
  data: unknown,
  fallback: string,
): string {
  return getUserFacingSpErrorMessage(
    getApiBodyErrorMessage(data, ""),
    fallback,
  );
}

/** Business validation failure — not an app crash. Callers must not console.error(this). */
export class ApiBusinessError extends Error {
  readonly isApiBusinessError = true as const;

  constructor(message: string) {
    super(message);
    this.name = "ApiBusinessError";
  }
}

export function isApiBusinessError(
  error: unknown,
): error is ApiBusinessError {
  return Boolean(
    error &&
      typeof error === "object" &&
      (error as { isApiBusinessError?: boolean }).isApiBusinessError === true,
  );
}

/** Throws ApiBusinessError when response body `status === "error"`. */
export function assertApiBodyOk(data: unknown, fallback: string): void {
  if (isApiBodyStatusError(data)) {
    throw new ApiBusinessError(getApiBodyErrorMessage(data, fallback));
  }
}

/**
 * Message for WarningModal when the failure is a business error.
 * Returns null for HTTP 500 / other non-business errors (use global handler).
 */
export function getApiBusinessErrorForModal(
  error: unknown,
  fallback: string,
): string | null {
  if (!isApiBusinessError(error)) return null;
  return getUserFacingSpErrorMessage(error.message, fallback);
}

/**
 * Do not console.error ApiBusinessError — Next.js treats that as a "1 Issue" overlay.
 * Use for catch blocks around assertApiBodyOk.
 */
export function logNonBusinessError(context: string, error: unknown): void {
  if (isApiBusinessError(error)) return;
  console.error(context, error);
}
